"use client"

import * as React from "react"
import { useLocation } from "react-router-dom"

import { BrokerAccountSwitcher } from "@/components/broker-account-switcher"
import { DashboardIcon } from "@/components/Icons/Icons"
import { NavAccountSettings } from "@/components/nav-account-settings"
import { NavNotificationsCentre } from "@/components/nav-notifications-centre"
import { NavMain } from "@/components/nav-main"
import { isAccountSettingsPathname } from "@/constants/accountSettingsSidebar"
import {
  isNotificationsCentrePathname,
} from "@/constants/notificationsCentre"
import { NavUser } from "@/components/nav-user"
import { SidebarResyncButton } from "@/components/sidebar-resync-button"
import { SidebarSupportButtons } from "@/components/sidebar-support-buttons"
import { BrainIcon } from "@/components/ui/brain"
import { ChartSplineIcon } from "@/components/ui/chart-spline"
import { FileChartLineIcon } from "@/components/ui/file-chart-line"
import { GalleryVerticalEndIcon } from "@/components/ui/gallery-vertical-end"
import { LayoutPanelTopIcon } from "@/components/ui/layout-panel-top-icon"
import { LayersIcon } from "@/components/ui/layers"
import { PlayIcon } from "@/components/ui/play"
import { SettingsMotionIcon } from "@/components/ui/settings-motion"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { useFeatureFlags } from "@/hooks/useFeatureFlags"
import { isWorkspaceEnabledForUser, shouldHideLegacyPipelineNav } from "@/utils/workspaceFeatureFlag"

const LEGACY_PIPELINE_PATHS = new Set(["/reports", "/simulator", "/strategies"]);

const baseNavMain = [
    { title: "Dashboard", url: "/dashboard", icon: <DashboardIcon color="inherit" /> },
    { title: "Trade Log", url: "/trade-log", icon: <GalleryVerticalEndIcon color="inherit" /> },
    { title: "Statistics", url: "/statistics", icon: <ChartSplineIcon color="inherit" /> },
    { title: "Reports", url: "/reports", icon: <FileChartLineIcon color="inherit" /> },
    {
      title: "Workspace",
      url: "/workspace",
      icon: <LayoutPanelTopIcon size={22} />,
    },
    {
      title: "Simulator",
      url: "/simulator",
      icon: <PlayIcon color="inherit" />,
    },
    { title: "Strategies", url: "/strategies", icon: <LayersIcon color="inherit" /> },
    { title: "University", url: "/university", icon: <BrainIcon color="inherit" /> },
    { title: "Admin", url: "/system", icon: <SettingsMotionIcon color="inherit" /> },
]

/**
 * Shadcn app sidebar (`DashboardShell` → `AppSidebar`; sheet on mobile, rail + inset at md+).
 * Legacy Chakra rail is `Sidebar` / `SidebarContent`, not this file.
 */
export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation()
  const { isMobile } = useSidebar()
  const { isAdmin } = useFeatureFlags()
  const accountSettingsView = isAccountSettingsPathname(pathname)
  const notificationsCentreView = isNotificationsCentrePathname(pathname)

  if (notificationsCentreView) {
    return <NavNotificationsCentre {...props} />
  }
  const navMain = React.useMemo(
    () =>
      baseNavMain.filter((item) => {
        if (item.url === "/system" && !isAdmin) {
          return false
        }
        if (item.url === "/workspace" && !isWorkspaceEnabledForUser(isAdmin)) {
          return false
        }
        if (shouldHideLegacyPipelineNav(isAdmin) && LEGACY_PIPELINE_PATHS.has(item.url)) {
          return false
        }
        return true
      }),
    [isAdmin]
  )

  return (
    <Sidebar data-testid="shadcn-app-sidebar" {...props}>
      <SidebarHeader>
        <div
          className={cn(
            "flex w-full min-h-0 items-center",
            "group-data-[state=expanded]:h-12",
            "group-data-[state=collapsed]:h-8"
          )}
        >
          <BrokerAccountSwitcher />
        </div>
      </SidebarHeader>
      <SidebarContent>
        {accountSettingsView ? (
          <NavAccountSettings />
        ) : (
          <NavMain items={navMain} />
        )}
      </SidebarContent>
      <SidebarFooter className="gap-2">
        <SidebarSupportButtons />
        <SidebarResyncButton />
        <NavUser />
      </SidebarFooter>
      {!isMobile ? <SidebarRail /> : null}
    </Sidebar>
  )
}
